import React, { useCallback, useRef } from 'react'
import { useEditor } from './EditorContext'
import DesignerChatPanel from './DesignerChatPanel'

function stableStringify (obj) {
  if (obj === null || obj === undefined) return JSON.stringify(obj)
  if (typeof obj !== 'object') return JSON.stringify(obj)
  if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`
  const keys = Object.keys(obj).sort()
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`
}

function detectPipelineProposal (code, language) {
  if (language !== 'json') return null
  let parsed
  try { parsed = JSON.parse(code) } catch { return null }
  const components = Array.isArray(parsed) ? parsed : parsed?.components
  if (!Array.isArray(components) || components.length === 0) return null
  return {
    icon: '✦',
    label: `Pipeline proposal — ${components.length} component${components.length === 1 ? '' : 's'} — click to review`,
    appliedLabel: 'Proposal opened — review in editor',
    payload: code
  }
}

function PipelineChatPanel () {
  const { code, pipelineText, pipelineParse, variables, proposeFromChat } = useEditor()

  // Track last-sent values so we only re-attach context when it actually changed.
  // Initial values are null, so the first send on any thread always includes
  // current pipeline + test inputs.
  const lastSentPipelineRef = useRef(null)
  const lastSentVariablesRef = useRef(null)
  const lastSentCodeRef = useRef(null)

  const buildAttachments = useCallback(() => {
    const pipelineChanged = pipelineText !== lastSentPipelineRef.current
    const variablesChanged = stableStringify(variables) !== stableStringify(lastSentVariablesRef.current)
    const codeChanged = code !== lastSentCodeRef.current

    // Update refs synchronously before the mutation kicks off so a re-render
    // during the in-flight request can't double-send the same context.
    lastSentPipelineRef.current = pipelineText
    lastSentVariablesRef.current = variables
    lastSentCodeRef.current = code

    if (!pipelineChanged && !variablesChanged && !codeChanged) return []

    const attachments = []
    if (!pipelineParse.error && pipelineParse.value) {
      const components = Array.isArray(pipelineParse.value) ? pipelineParse.value : pipelineParse.value?.components
      if (Array.isArray(components) && components.length > 0) {
        attachments.push({
          name: 'pipeline',
          content: components,
          ...(code ? { metadata: { pipelineCode: code } } : {})
        })
      }
    }

    const filters = variables?.filters || {}
    const dates = variables?.dates || {}
    if (Object.keys(filters).length > 0 || Object.keys(dates).length > 0) {
      attachments.push({ name: 'testInputs', content: { filters, dates } })
    }

    return attachments
  }, [code, pipelineText, pipelineParse, variables])

  const applyProposal = useCallback((proposal) => {
    if (!proposal?.payload) return false
    return proposeFromChat(proposal.payload)
  }, [proposeFromChat])

  return (
    <DesignerChatPanel
      agentId='ddp-designer'
      title='Designer'
      placeholder='Describe what you want to build or change…'
      buildAttachments={buildAttachments}
      detectProposal={detectPipelineProposal}
      applyProposal={applyProposal}
    />
  )
}

export default PipelineChatPanel
